/* eslint-disable */
// Factory filter matching — handles the format drift introduced by the
// Factory Sync feature (commit e80ef31). The DB has 1,880+ rows with the
// old format ("IP", "SK", "LN") alongside a handful with the new format
// ("IP — Inter", "SK — Sunrise"). A literal equality filter would show
// only 4 rows instead of 1,090 when the user selects "IP — Inter".
//
// Fix: extract a canonical UPPERCASE code from any format (old, new, or
// free-text like "Hivison"/"Core") and compare on the code. Em-dash is
// the canonical separator produced by Factory Sync, but a hyphen is
// also accepted defensively.
//
// Zero DB writes. Pure client-side matching.

/**
 * Extracts a canonical UPPERCASE factory code from any format seen in the wild.
 *
 * Examples:
 *   "IP"             → "IP"
 *   "IP — Inter"     → "IP"     (em-dash split)
 *   "IP-Inter"       → "IP"     (hyphen split)
 *   "iP"             → "IP"     (case-insensitive)
 *   "  IP — Inter  " → "IP"     (trimmed)
 *   "Other"          → "OTHER"
 *   "Hivison"        → "HIVISON"
 *   null / undefined / "" → ""
 */
export function extractFactoryCode(value) {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  const m = s.match(/^([A-Za-z]+)\s*[—-]/);
  if (m) return m[1].toUpperCase();
  return s.toUpperCase();
}

/**
 * True if a dev's stored factory value matches a selected filter value.
 * Match is by canonical code, so "IP" and "IP — Inter" resolve to the
 * same factory. Empty filterValue → match all (no filter).
 */
export function factoryMatches(devFty, filterValue) {
  if (!filterValue) return true;
  return extractFactoryCode(devFty) === extractFactoryCode(filterValue);
}

/**
 * Collapses a factory options list so one entry survives per canonical code.
 * When both a bare code ("IP") and a full-format label ("IP — Inter")
 * exist for the same code, the full format wins — clearer UX in the
 * dropdown. Free-text entries without a dash stay as themselves.
 * Iteration order of the underlying Map preserves first-insertion order
 * per canonical code.
 */
export function dedupeFactoryOptions(options) {
  if (!Array.isArray(options)) return [];
  const byCode = new Map();
  for (const opt of options) {
    if (opt == null) continue;
    const code = extractFactoryCode(opt);
    if (!code) continue;
    const existing = byCode.get(code);
    if (existing == null) {
      byCode.set(code, opt);
      continue;
    }
    // Prefer the full-format variant (contains em-dash) when a
    // competing bare-code entry is already in the map.
    const existingFull = typeof existing === 'string' && existing.includes('—');
    const candidateFull = typeof opt === 'string' && opt.includes('—');
    if (candidateFull && !existingFull) byCode.set(code, opt);
  }
  return Array.from(byCode.values());
}
